import { Button, CopyButton, TruncatedContent } from "@aurem/ui";
import { ExternalLink } from "lucide-react";
import { ReactNode, useCallback } from "react";
import { useAuth } from "../../../providers/AuthProvider";
import { DevToolbarCard } from "../components/DevToolbarCard";
import { TenantSelectorCard } from "../components/TenantSelectorCard";

const API_URL = process.env.REACT_APP_GRAPHQL_API ?? "";
const GRAPHQL_EXPLORER_URL = `https://studio.apollographql.com/sandbox/explorer?endpoint=${encodeURIComponent(API_URL)}`;

type InfoCardProps = {
  label: string;
  value: string;
  displayValue?: ReactNode;
  showCopy?: boolean;
  openUrl?: string;
  testId: string;
};

const InfoCard = ({
  label,
  value,
  displayValue,
  showCopy = true,
  openUrl,
  testId,
}: InfoCardProps) => {
  const openInNewTab = useCallback((url: string) => {
    window.open(url, "_blank", "noopener,noreferrer");
  }, []);

  return (
    <DevToolbarCard title={label} testId={testId}>
      <div className="flex items-center gap-2">
        <TruncatedContent>{displayValue ?? value}</TruncatedContent>
        {showCopy && (
          <CopyButton
            copyContent={value}
            variant="ghost"
            color="neutral"
            size="sm"
            data-e2e-id={`${testId}-copy`}
          />
        )}
        {openUrl && (
          <Button
            variant="ghost"
            color="neutral"
            size="sm"
            onClick={() => openInNewTab(openUrl)}
            startIcon={<ExternalLink size={14} />}
            data-e2e-id={`${testId}-open`}
          />
        )}
      </div>
    </DevToolbarCard>
  );
};

export const ApiTab = () => {
  const { authState } = useAuth();

  return (
    <div
      className="px-4 py-3 h-full grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-3"
      data-e2e-id="devtoolbar-api-tab"
    >
      <InfoCard
        label="GraphQL API"
        value={API_URL}
        showCopy
        openUrl={API_URL}
        testId="devtoolbar-graphql-api"
      />
      <InfoCard
        label="GraphQL Explorer"
        value={GRAPHQL_EXPLORER_URL}
        displayValue="Apollo Studio Sandbox"
        showCopy
        openUrl={GRAPHQL_EXPLORER_URL}
        testId="devtoolbar-graphql-explorer"
      />
      <InfoCard
        label="JWT Access Token"
        value={authState.accessToken ?? ""}
        displayValue={
          authState.accessToken
            ? `${authState.accessToken.slice(0, 40)}...`
            : "No token available"
        }
        showCopy={!!authState.accessToken}
        testId="devtoolbar-jwt-token"
      />
      <TenantSelectorCard />
    </div>
  );
};
